import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SignOutOnError } from '@platform-frontend/account';
import {
  DeletePaymentMethod,
  FetchPaymentMethods,
  isReusablePaymentMethod,
  isReusablePaymentMethodIdentifier,
  MARKETPLACE_ERROR_HANDLERS,
  PaymentMethod,
  PaymentMethodCreated,
  PaymentMethodIdentifier,
  PaymentMethodInfo,
  PaymentMethodRedirection,
  PaymentMethodUpdated,
  ProductIdentifier,
  ReusablePaymentMethod,
  ScaService,
  WalletPaymentMethod
} from '@platform-frontend/marketplace';
import { CreditCardType } from '@platform-frontend/payment-util';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import {
  ClearNotifications,
  convertToHttpParams,
  handleAllErrors,
  handleErrors,
  InvalidStateError,
  PlannedError,
  RefreshWindow,
  ShowErrorNotification,
  ShowSuccessNotification,
  ShowWarningNotification,
  throwIfNullish
} from '@platform-frontend/util';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { AppFlowType, FetchFlowData, FlowCanceled, FlowState, PublishFetchedFlowData, SetFlow } from '../../flow';
import {
  MSG_CONNECT_AUTHORIZATION_REFUSED,
  MSG_CONNECT_PAYMENT_METHOD_DEFAULT,
  MSG_CONNECT_PAYMENT_METHOD_PREVENT_SCA,
  MSG_CONNECT_PAYMENT_METHOD_SUCCESS,
  MSG_CONNECT_PAYMENT_REFRESHING_PAYMENT_METHODS_FAILED,
  MSG_CONNECT_REDIRECT_ID_NOT_KNOWN,
  MSG_UPDATE_PAYMENT_METHOD_SUCCESS
} from '../../notification-messages';
import { CheckoutPaymentInformationState } from '../../payment-information';
import { BackendPaymentInformation, WalletPaymentDataService } from '../../payment-wallet';
import { ProductState } from '../../product';
import { AssetState } from '../asset.state';
import { SubscriptionPaymentStateModel } from './model/subscription-payment-state-model';
import { ConnectPaymentMethod, DeleteConnectedPaymentMethod } from './subscription-payment.actions';

const DEFAULT_STATE: SubscriptionPaymentStateModel = {
  connectedPaymentMethod: null,
  loadedConnectedPaymentMethod: null
};

@State<SubscriptionPaymentStateModel>({
  name: 'subscriptionPayment',
  defaults: DEFAULT_STATE
})
@Injectable()
export class SubscriptionPaymentState {
  constructor(private http: HttpClient, private walletPaymentDataService: WalletPaymentDataService, private scaService: ScaService, private store: Store) {}

  paymentMethods$: Observable<PaymentMethodInfo[]> = this.store.select(CheckoutPaymentInformationState.paymentMethods);
  productIdentifier$: Observable<ProductIdentifier | null> = this.store.select(ProductState.productIdentifier);
  contractId$: Observable<string | null> = this.store.select(AssetState.contractId);
  flowType$: Observable<AppFlowType> = this.store.select(FlowState.flowType);

  @Selector()
  static connectedPaymentMethod(state: SubscriptionPaymentStateModel): PaymentMethod | null {
    return state.connectedPaymentMethod;
  }

  @Selector([CheckoutPaymentInformationState.paymentMethods, SubscriptionPaymentState.connectedPaymentMethod])
  static paymentMethods(
    state: SubscriptionPaymentStateModel,
    paymentMethods: PaymentMethodInfo[],
    connectedPaymentMethod: PaymentMethod | null
  ): (PaymentMethodInfo | PaymentMethod)[] {
    return connectedPaymentMethod && !isReusablePaymentMethod(connectedPaymentMethod)
      ? this.getPaymentMethodsWithConnectedWallet(connectedPaymentMethod, paymentMethods)
      : paymentMethods;
  }

  private static getPaymentMethodsWithConnectedWallet(wallet: WalletPaymentMethod, paymentMethods: PaymentMethodInfo[]): (PaymentMethodInfo | PaymentMethod)[] {
    const index = paymentMethods.findIndex(method => method.type === wallet.type);
    return [...paymentMethods.slice(0, index), wallet, ...paymentMethods.slice(index + 1)];
  }

  @Selector([SubscriptionPaymentState.connectedPaymentMethod, SubscriptionPaymentState.paymentMethods])
  static canConnectedPaymentMethodBeReplaced(
    state: SubscriptionPaymentStateModel,
    connectedPaymentMethod: PaymentMethod | null,
    paymentMethods: (PaymentMethodInfo | PaymentMethod)[]
  ): boolean {
    return this.getFirstMethodAfterDeletion(paymentMethods, connectedPaymentMethod) !== null;
  }

  private static getFirstMethodAfterDeletion(paymentMethods: PaymentMethodInfo[], connectedPaymentMethod: PaymentMethod | null): ReusablePaymentMethod | null {
    const applicableMethods: ReusablePaymentMethod[] = paymentMethods.filter(
      (method): method is ReusablePaymentMethod =>
        isReusablePaymentMethod(method) &&
        (!connectedPaymentMethod || !isReusablePaymentMethod(connectedPaymentMethod) || method.id !== connectedPaymentMethod.id)
    );
    return applicableMethods.length > 0 ? applicableMethods[0] : null;
  }

  @Action(FetchFlowData)
  loadConnectedPaymentMethod(ctx: StateContext<SubscriptionPaymentStateModel>, { flow }: FetchFlowData): Observable<unknown> {
    if (flow.type !== 'SUBSCRIPTION_MANAGEMENT') {
      return of(null);
    }

    return this.fetchConnectedPaymentMethodFromBackend(flow.startParams.productIdentifier, flow.startParams.contractId).pipe(
      tap(loadedConnectedPaymentMethod => ctx.patchState({ loadedConnectedPaymentMethod }))
    );
  }

  @Action(PublishFetchedFlowData)
  publishConnectedPaymentMethod(ctx: StateContext<SubscriptionPaymentStateModel>): void {
    const { loadedConnectedPaymentMethod } = ctx.getState();
    ctx.patchState({
      connectedPaymentMethod: loadedConnectedPaymentMethod,
      loadedConnectedPaymentMethod: DEFAULT_STATE.loadedConnectedPaymentMethod
    });
  }

  @Action(SetFlow)
  @Action(RefreshWindow)
  @Action(FlowCanceled)
  reset(ctx: StateContext<SubscriptionPaymentStateModel>): void {
    ctx.setState(DEFAULT_STATE);
  }

  private fetchConnectedPaymentMethodFromBackend(productIdentifier: ProductIdentifier, contractId: string | null): Observable<PaymentMethod> {
    const params = convertToHttpParams({ ...productIdentifier, contractId });
    return this.http.get<PaymentMethod>(PLATFORM_ENVIRONMENT.apiUrl + '/subscription/paymentInformation', { params });
  }

  @Action(ConnectPaymentMethod)
  connectPaymentMethod(ctx: StateContext<SubscriptionPaymentStateModel>, { connectPaymentData, mode }: ConnectPaymentMethod): Observable<unknown> {
    return combineLatest([
      this.productIdentifier$.pipe(throwIfNullish(() => new InvalidStateError(`Product identifier not available.`))),
      this.contractId$
    ]).pipe(
      take(1),
      switchMap(([productIdentifier, contractId]) => this.connectPaymentMethodForOrder(ctx, productIdentifier, contractId, connectPaymentData, mode))
    );
  }

  private connectPaymentMethodForOrder(
    ctx: StateContext<SubscriptionPaymentStateModel>,
    productIdentifier: ProductIdentifier,
    contractId: string | null,
    connectPaymentData: BackendPaymentInformation,
    mode: 'default' | 'deletion' = 'default'
  ): Observable<unknown> {
    return this.http
      .put<PaymentMethodRedirection | null>(PLATFORM_ENVIRONMENT.apiUrl + '/subscription/paymentInformation/connect', {
        contractId,
        productIdentifier,
        paymentInformation: connectPaymentData
      })
      .pipe(
        switchMap(redirection => this.performScaIfNecessary(connectPaymentData, redirection, mode)),
        handleErrors(MARKETPLACE_ERROR_HANDLERS, {
          DEFAULT: new ShowErrorNotification(MSG_CONNECT_PAYMENT_METHOD_DEFAULT),

          // sca errors
          REDIRECT_ID_NOT_KNOWN: new ShowErrorNotification(MSG_CONNECT_REDIRECT_ID_NOT_KNOWN),
          AUTHORIZATION_REFUSED: new ShowErrorNotification(MSG_CONNECT_AUTHORIZATION_REFUSED),
          CANCELED_BY_USER: () => undefined
        }),
        switchMap(() => this.fetchPaymentMethodsAfterConnection(ctx, productIdentifier, contractId)),
        switchMap(() => ctx.dispatch([new ClearNotifications(), new ShowSuccessNotification(MSG_CONNECT_PAYMENT_METHOD_SUCCESS)]))
      );
  }

  private fetchPaymentMethodsAfterConnection(
    ctx: StateContext<SubscriptionPaymentStateModel>,
    productIdentifier: ProductIdentifier,
    contractId: string | null
  ): Observable<unknown> {
    // Patch connected payment method immediatley after fetching payment methods to avoid flickering
    return this.fetchConnectedPaymentMethodFromBackend(productIdentifier, contractId).pipe(
      switchMap(connectedPaymentMethod => ctx.dispatch(new FetchPaymentMethods()).pipe(tap(() => ctx.patchState({ connectedPaymentMethod })))),
      handleAllErrors(new SignOutOnError(new ShowWarningNotification(MSG_CONNECT_PAYMENT_REFRESHING_PAYMENT_METHODS_FAILED)))
    );
  }

  private performScaIfNecessary(
    connectPaymentData: BackendPaymentInformation,
    redirection: PaymentMethodRedirection | null,
    mode: 'default' | 'deletion'
  ): Observable<unknown> {
    if (redirection === null) {
      return of(null);
    }

    if (mode === 'deletion') {
      return throwError(() => new PlannedError('SCA_PREVENTED_ON_DELETION'));
    }

    return this.getCreditCardTypeForSca(connectPaymentData).pipe(
      switchMap(cardType => this.scaService.performSca(redirection, cardType, 'selection').pipe(handleAllErrors(new FetchPaymentMethods())))
    );
  }

  private getCreditCardTypeForSca(connectPaymentData: BackendPaymentInformation): Observable<CreditCardType> {
    if (!isReusablePaymentMethodIdentifier(connectPaymentData)) {
      if (connectPaymentData.type === 'APPLE_PAY') {
        throw new InvalidStateError('SCA requested for Apple Pay');
      }
      const cardType = this.walletPaymentDataService.mapGooglePayPaymentDataToCreditCardType(connectPaymentData);
      return of(cardType);
    }

    return this.paymentMethods$.pipe(
      take(1),
      map(paymentMethods => paymentMethods.find(paymentMethod => isReusablePaymentMethod(paymentMethod) && paymentMethod.id === connectPaymentData.id) ?? null),
      map(paymentMethod => (paymentMethod && paymentMethod.type === 'CREDIT_CARD' ? paymentMethod.cardType : null)),
      throwIfNullish(() => new InvalidStateError('SCA requested for unsupported payment method'))
    );
  }

  @Action(DeleteConnectedPaymentMethod)
  deleteConnectedPaymentMethod(ctx: StateContext<SubscriptionPaymentStateModel>): Observable<unknown> {
    const connectedId = this.getPaymentMethodIdentifier(ctx);
    return this.paymentMethods$.pipe(
      take(1),
      map(paymentMethods => this.getAlternativeMethodAfterDeletion(ctx, paymentMethods)),
      map(({ id, serversideVersion }) => ({ id, serversideVersion })),
      switchMap(paymentIdentifier => ctx.dispatch(new ConnectPaymentMethod(paymentIdentifier, 'deletion'))),
      switchMap(() => ctx.dispatch(new DeletePaymentMethod(connectedId))),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        SCA_PREVENTED_ON_DELETION: new ShowWarningNotification(MSG_CONNECT_PAYMENT_METHOD_PREVENT_SCA)
      })
    );
  }

  private getPaymentMethodIdentifier(ctx: StateContext<SubscriptionPaymentStateModel>): PaymentMethodIdentifier {
    const connectedMethod = ctx.getState().connectedPaymentMethod;
    if (connectedMethod) {
      return isReusablePaymentMethod(connectedMethod) ? { id: connectedMethod.id } : { type: connectedMethod.type };
    } else {
      throw new InvalidStateError('No payment method connected');
    }
  }

  private getAlternativeMethodAfterDeletion(ctx: StateContext<SubscriptionPaymentStateModel>, paymentMethods: PaymentMethodInfo[]): ReusablePaymentMethod {
    const alternativePaymentMethod = SubscriptionPaymentState.getFirstMethodAfterDeletion(paymentMethods, ctx.getState().connectedPaymentMethod);
    if (alternativePaymentMethod !== null) return alternativePaymentMethod;
    throw new InvalidStateError('Cannot delete last applicable payment method');
  }

  @Action(PaymentMethodCreated)
  autoSelectCreatedPaymentMethod(ctx: StateContext<SubscriptionPaymentStateModel>, { paymentMethod }: PaymentMethodCreated): Observable<unknown> {
    return this.flowType$.pipe(
      take(1),
      switchMap(flowType =>
        flowType === 'SUBSCRIPTION_MANAGEMENT' && isReusablePaymentMethod(paymentMethod)
          ? ctx.dispatch(new ConnectPaymentMethod({ id: paymentMethod.id, serversideVersion: paymentMethod.serversideVersion }))
          : of(null)
      )
    );
  }

  @Action(PaymentMethodUpdated)
  updateOnUpdate(ctx: StateContext<SubscriptionPaymentStateModel>, { original, update, changed }: PaymentMethodUpdated): Observable<unknown> {
    const connectedPaymentMethod = ctx.getState().connectedPaymentMethod;
    if (connectedPaymentMethod && isReusablePaymentMethod(connectedPaymentMethod) && connectedPaymentMethod.id === original.id) {
      ctx.patchState({ connectedPaymentMethod: update });
      return changed ? ctx.dispatch(new ShowSuccessNotification(MSG_UPDATE_PAYMENT_METHOD_SUCCESS)) : of(null);
    }
    return of(null);
  }
}
